import ResourceForm from './ResourceForm';
import { useParams } from 'react-router-dom';
import { useRef, useState, useReducer, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import validate from '../../../validation/schemas/add-custom-resource';
import {
  useUpdateCustomTrainingResourceById,
  useGetCustomTrainingResource,
  useDeleteCustomTrainingResourceById,
  useGetAllCustomTrainingResources,
} from '../../../api-calls/custom-training.queries';
import { useAuth } from '../../../context/auth';
import { navRoutes } from '../../../constants';
import DeleteConfirmModal from './DeleteConfirmModal';
import SuccessModal from './SuccessModal';
import { Col, Row } from 'components/Grid';
import { BackButton } from 'components/BackButton';
function reducer(state, newState) {
  let value = newState;
  if (typeof newState === 'function') {
    value = newState(state);
  }
  return { ...state, ...value };
}

const initialState = {
  form: {
    title: '',
    externalLink: '',
    order: 1,
  },
  httpError: '',
  validationErrs: {},
};

const TrainingCustomizationEditResources = () => {
  const { user } = useAuth();
  const [isEditMode, setIsEditMode] = useState(true);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const { resourceId } = useParams();
  const navigate = useNavigate();
  const submitAttempt = useRef(false);

  const resources = useGetAllCustomTrainingResources({
    organisationId: user.organisationId,
  });

  const {
    mutateAsync: deleteStep,
    error: deleteError,
    isLoading: deleteLoading,
  } = useDeleteCustomTrainingResourceById({
    id: resourceId,
  });

  const handleDelete = async () => {
    try {
      setIsEditMode(false);
      await deleteStep();
      setIsConfirmModalVisible(false);
      setIsSuccessModalVisible(true);
    } catch (error) {
      setState({
        httpError: error?.response?.data?.message,
        validationErrs: { ...validationErrs },
      });
    }
  };

  const { data: stepData, isLoading: stepDataLoading } =
    useGetCustomTrainingResource({
      id: resourceId,
    });

  useEffect(() => {
    if (!stepDataLoading && stepData) {
      setState((prevState) => ({
        form: {
          ...prevState.form,
          title: stepData?.title,
          externalLink: stepData?.externalLink,
          order: stepData?.order,
        },
      }));
    }
  }, [stepData, stepDataLoading]);

  const [state, setState] = useReducer(reducer, initialState);
  const { validationErrs, form } = state;

  const {
    mutateAsync,
    error: httpError,
    isLoading,
  } = useUpdateCustomTrainingResourceById();

  const setFormData = (data) =>
    setState((prevState) => ({
      form: { ...prevState.form, ...data },
    }));

  const validateForm = useCallback(() => {
    try {
      validate({
        ...state.form,
      });
      setState({ validationErrs: { hasError: false }, httpError: false });
      return true;
    } catch (error) {
      if (error.name === 'ValidationError') {
        setState({
          validationErrs: { ...error.inner, hasError: true },
        });
      }
      return false;
    }
  }, [state.form]);

  const handleSubmit = async (e) => {
    setIsEditMode(true);
    e.preventDefault();
    submitAttempt.current = true;

    const isValid = validateForm();

    if (isValid) {
      try {
        await mutateAsync({
          ...state.form,
          organisationId: user.organisationId,
          id: resourceId,
        });
        setIsSuccessModalVisible(true);
      } catch (error) {
        setState({
          httpError: error?.response?.data?.message,
          validationErrs: { ...validationErrs },
        });
      }
    }
  };

  useEffect(() => {
    if (submitAttempt.current) {
      validateForm();
    }
  }, [form, validateForm]);

  return (
    <Row jc="center">
      <Col w={[4, 10, 10]}>
        <BackButton link={navRoutes.FACILITATOR.TRAINING_CUSTOMIZATION} />
      </Col>
      <Col w={[4, 10, 10]}>
        <ResourceForm
          formType="edit"
          setFormData={setFormData}
          form={form}
          validationErrs={validationErrs}
          httpError={httpError?.message || deleteError?.message}
          loading={isLoading || deleteLoading}
          handleSubmit={handleSubmit}
          handleDelete={() => setIsConfirmModalVisible(true)}
          userRole={user.role}
          orderDropdownOptions={resources?.customTrainingResources?.map(
            (_, index) => ({
              value: index + 1,
              label: `${index + 1}`,
            })
          )}
        />
      </Col>
      <DeleteConfirmModal
        message="Once you have deleted this resource this cannot be undone."
        onCancel={() => setIsConfirmModalVisible(false)}
        onConfirm={handleDelete}
        isModalVisible={isConfirmModalVisible}
        setIsModalVisible={setIsConfirmModalVisible}
      />
      <SuccessModal
        message={
          isEditMode
            ? 'You have edited this resource from your coaches’ training.'
            : 'You have deleted this resource from your coaches’ training.'
        }
        isModalVisible={isSuccessModalVisible}
        setIsModalVisible={setIsSuccessModalVisible}
        onSuccess={() => navigate(navRoutes.FACILITATOR.TRAINING_CUSTOMIZATION)}
      />
    </Row>
  );
};

export default TrainingCustomizationEditResources;
